import { faBrain, faClose, faKey, faLink, faRobot, faServer } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { EWhatsAppTransportType } from "../../../Objects/PlatformWhatsApp/EWhatsAppTransportType"
import type { IPlatformWhatsAppSettings } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppSettings"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { RESPOSTA_AI_DEFAULT_BASE_URL } from "./PlatformWhatsAppRespostaAiSettingsPopup"

type Props = {
  settings: IPlatformWhatsAppSettings
  onChange: (value: IPlatformWhatsAppSettings) => void
  onSave: () => void
  onClose: () => void
  onOpenRespostaAiSettings: () => void
}

export const PlatformWhatsAppSettingsPopup = ({
  settings,
  onChange,
  onSave,
  onClose,
  onOpenRespostaAiSettings,
}: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  const patch = (value: Partial<IPlatformWhatsAppSettings>) => onChange({ ...settings, ...value })

  const isRespostaAiConfigured = Boolean(
    settings.whatsAppSaaSInstanceId && settings.hasWhatsAppSaaSApiKey && settings.hasWhatsAppSaaSWebhookSecret,
  )

  const isRespostaAiActive = settings.activeTransport === EWhatsAppTransportType.WhatsAppSaaS

  const selectDesktopAgent = () => patch({ activeTransport: EWhatsAppTransportType.DesktopAgent })

  const selectRespostaAi = () => {
    patch({
      activeTransport: EWhatsAppTransportType.WhatsAppSaaS,
      whatsAppSaaSBaseUrl: settings.whatsAppSaaSBaseUrl || RESPOSTA_AI_DEFAULT_BASE_URL,
    })
    onOpenRespostaAiSettings()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("settings.title")}
              icon={faBrain}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <div className="grid max-h-[75vh] gap-3 overflow-x-hidden overflow-y-auto p-4 md:grid-cols-2">
              {!isRespostaAiActive && (
                <>
                  <SimpleToggle
                    title={t("settings.enabled")}
                    value={settings.isEnabled}
                    onChange={(value) => patch({ isEnabled: value })}
                    animationDuration="1.1s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.toggle.enabled"
                  />

                  <GenericDropDown
                    legend={t("settings.provider")}
                    initialState={String(settings.provider)}
                    data={[
                      { label: t("settings.gpt"), value: "0" },
                      { label: t("settings.cloud"), value: "1" },
                    ]}
                    handlerFuncion={(event) => patch({ provider: Number(event.target.value) })}
                    animationDuration="1.15s"
                    delay="0.03s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.select.provider"
                  />

                  <InputField
                    type="link"
                    icon={faLink}
                    value={settings.endpointUrl}
                    placeholder={t("settings.endpoint")}
                    onChange={(event) => patch({ endpointUrl: event.target.value })}
                    animationDuration="1.2s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.endpoint"
                  />

                  <InputField
                    type="password"
                    icon={faKey}
                    value={settings.apiKey}
                    placeholder={settings.hasApiKey ? t("settings.keyStored") : t("settings.apiKey")}
                    onChange={(event) => patch({ apiKey: event.target.value })}
                    animationDuration="1.25s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.keyStored"
                  />

                  <InputField
                    type="text"
                    icon={faRobot}
                    value={settings.model}
                    placeholder={t("settings.model")}
                    onChange={(event) => patch({ model: event.target.value })}
                    animationDuration="1.3s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.model"
                  />

                  <InputField
                    type="text"
                    icon={faRobot}
                    value={settings.botDisplayName}
                    placeholder={t("settings.botName")}
                    onChange={(event) => patch({ botDisplayName: event.target.value })}
                    animationDuration="1.35s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.botName"
                  />

                  <InputField
                    type="link"
                    value={settings.signupUrl}
                    placeholder={t("settings.signupUrl")}
                    onChange={(event) => patch({ signupUrl: event.target.value })}
                    animationDuration="1.4s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.signupUrl"
                  />

                  <InputField
                    type="link"
                    value={settings.tutorialsUrl}
                    placeholder={t("settings.tutorialsUrl")}
                    onChange={(event) => patch({ tutorialsUrl: event.target.value })}
                    animationDuration="1.45s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.tutorialsUrl"
                  />

                  <InputField
                    type="number"
                    value={String(settings.maximumReplyCharacters)}
                    placeholder={t("settings.maxReply")}
                    onChange={(event) => patch({ maximumReplyCharacters: Number(event.target.value) || 1800 })}
                    animationDuration="1.5s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.maxReply"
                  />

                  <InputField
                    type="text"
                    value={settings.humanHandoffKeywords}
                    placeholder={t("settings.handoff")}
                    onChange={(event) => patch({ humanHandoffKeywords: event.target.value })}
                    animationDuration="1.55s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.handoff"
                  />
                </>
              )}

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.58s">
                <div className="md:col-span-2 rounded-xl border-2 border-slate-200 bg-white p-3">
                  <p className="mb-3 text-sm font-semibold text-slate-700">{t("settings.typeSelector.title")}</p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={selectDesktopAgent}
                      className={`rounded-xl border-2 p-4 text-left transition ${
                        settings.activeTransport === EWhatsAppTransportType.DesktopAgent
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.button.selectDesktopAgent"
                    >
                      <FontAwesomeIcon icon={faServer} className="mb-2 text-lg text-slate-600" />

                      <p className="font-bold text-slate-800">{t("settings.typeSelector.desktopTitle")}</p>

                      <p className="text-xs text-slate-500">{t("settings.typeSelector.desktopDescription")}</p>
                    </button>

                    <button
                      type="button"
                      onClick={selectRespostaAi}
                      className={`rounded-xl border-2 p-4 text-left transition ${
                        settings.activeTransport === EWhatsAppTransportType.WhatsAppSaaS
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.button.selectRespostaAi"
                    >
                      <FontAwesomeIcon icon={faRobot} className="mb-2 text-lg text-slate-600" />

                      <p className="font-bold text-slate-800">{t("settings.typeSelector.respostaAiTitle")}</p>

                      <p className="text-xs text-slate-500">{t("settings.typeSelector.respostaAiDescription")}</p>

                      <span
                        className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          isRespostaAiConfigured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {isRespostaAiConfigured
                          ? t("settings.typeSelector.configured")
                          : t("settings.typeSelector.pending")}
                      </span>
                    </button>
                  </div>
                </div>
              </Animate>

              {isRespostaAiActive && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                  <p className="md:col-span-2 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    {t("respostaAi.localAiHiddenNote")}
                  </p>
                </Animate>
              )}

              {!isRespostaAiActive && (
                <>
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                    <label className="md:col-span-2 text-sm font-semibold text-slate-700">
                      {t("settings.personality")}

                      <textarea
                        className="mt-2 w-full rounded-xl border-2 border-slate-200 bg-white p-3 outline-none transition-all duration-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-500"
                        rows={3}
                        maxLength={2000}
                        value={settings.personality}
                        onChange={(event) => patch({ personality: event.target.value })}
                        data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.input"
                      />
                    </label>
                  </Animate>

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.65s">
                    <label className="md:col-span-2 text-sm font-semibold text-slate-700">
                      {t("settings.instructions")}

                      <textarea
                        className="mt-2 w-full rounded-xl border-2 border-slate-200 bg-white p-3 outline-none transition-all duration-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-500"
                        rows={5}
                        maxLength={10000}
                        value={settings.additionalInstructions}
                        onChange={(event) => patch({ additionalInstructions: event.target.value })}
                        data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.input.input2"
                      />
                    </label>
                  </Animate>
                </>
              )}

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.7s">
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="button"
                    className="rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-md active:scale-95"
                    onClick={onSave}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppSettingsPopup.button.onSave"
                  >
                    {t("actions.save")}
                  </button>
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
